/**
 * The fields a record type shows, in the order an admin configured them.
 *
 * TRUS-401 — the Household and Family Member forms were built against
 * `GET /api/custom-fields`, which lists com_fields only and orders them
 * `category, friendly_name` — i.e. ALPHABETICALLY. Everything an admin sets in
 * Field Configuration (display name, required, hidden, width and above all
 * sort_order) lives in `member_field_configurations` and never reached those
 * forms, so fields rendered in an order nobody chose and no amount of
 * reordering in the admin screen had any effect.
 *
 * The platform already has the right endpoint for this:
 * `GET /api/member-field-config/profile-fields` (the unified
 * `get_member_profile_fields` function) merges standard + custom fields, applies
 * the per-org configuration, and orders by (category_sort, field_sort). It has
 * accepted `record_type=household|family_member|caregiver` all along. This hook
 * is the missing consumer.
 */

import { useEffect, useState } from 'react';
import { membersAPI } from '@/services/api/members';
import type { RecordTypeScope } from '@/services/api/members';
import { toConfiguredField, type ConfiguredField } from '@/utils/configuredFields';

export { toConfiguredField } from '@/utils/configuredFields';
export type { ConfiguredField } from '@/utils/configuredFields';

export default function useConfiguredFields(opts: {
  record_type: RecordTypeScope;
  include_hidden?: boolean;
}): { fields: ConfiguredField[]; loading: boolean; error: string | null } {
  const [fields, setFields] = useState<ConfiguredField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { record_type, include_hidden } = opts;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    membersAPI
      .getProfileFields({ record_type, include_hidden: !!include_hidden })
      .then((rows) => {
        if (cancelled) return;
        // The endpoint already returns them ordered; preserve that order rather
        // than re-sorting, so the admin's arrangement is what renders.
        setFields((rows || []).map(toConfiguredField));
        setError(null);
      })
      .catch((e: any) => {
        if (cancelled) return;
        // A field list that fails to load must not blank the form it belongs to.
        console.warn('useConfiguredFields: failed to load', e);
        setFields([]);
        setError(e?.message || 'Failed to load fields');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [record_type, include_hidden]);

  return { fields, loading, error };
}
